import dayjs, { Dayjs } from 'dayjs'
import duration, { Duration } from 'dayjs/plugin/duration'
import mixpanel from 'mixpanel-browser'
import { useRecoilState } from 'recoil'
import {
    AccountRecord,
    ContentFeedView,
    SocialMediaServiceEnum,
    SocialMediaServiceView
} from '../../../api/client'
import { userState } from '../../../store/DomainStore'
import { getSocialMediaServiceEnum } from '../../../util/types'
import AdOrOrganicFilterComboBox from '../../AdOrOrganicComboBox'
import DmFilterComboBox from '../../DmFilterComboBox'
import ContentFeedEditDialog from '../../feeds/ContentFeedEditDialog'
import ReadReceiptFilterComboBox from '../../read-receipt/ReadReceiptFilterComboBox'
import SearchInput from '../../SearchInput'
import SummaryStatistics from '../../SummaryStatistics'
import HasMediaToggle from '../../toggles/HasMediaToggle'
import OwnRepliesToggle from '../../toggles/OwnRepliesToggle'
import TranslationSettings from '../../translation/TranslationSettings'
import AccountFilter from './AccountFilter'
import DateTimePickerContainer from './DateTimePickerContainer'
import GeneralClassifierFilter from './GeneralClassifierFilter'
import SearchParameters from './searchParameters'
import SocialMediaServiceFilter from './SocialMediaServiceFilter'
import TeamFilter from './TeamFilter'

dayjs.extend(duration)

type Props = {
    account?: AccountRecord
    allSocialMediaServices: SocialMediaServiceView[]
    contentFeed?: ContentFeedView
    searchParameters: SearchParameters
    onSearchParameterChange: (searchParams: SearchParameters, replace?: boolean) => void
    showProfileSearch: boolean
    showReadStatusSearch: boolean
    showSocialMediaServiceSearch: boolean
    showTeamSearch: boolean
}

export default function ContentSearchBar(props: Props) {
    const [user] = useRecoilState(userState)
    const {
        account,
        allSocialMediaServices,
        contentFeed,
        onSearchParameterChange,
        searchParameters,
        showProfileSearch,
        showReadStatusSearch,
        showSocialMediaServiceSearch,
        showTeamSearch
    } = props
    let socialMediaService: SocialMediaServiceEnum | undefined = undefined
    if (account?.socialMediaServiceShortName) {
        socialMediaService = getSocialMediaServiceEnum(account.socialMediaServiceShortName)
    }

    const showDmsSearch = account?.dmsEnabled || false

    return (
        <div className="px-4 py-4 flex flex-row flex-wrap gap-x-4 gap-y-4 ">
            {showTeamSearch && (
                <TeamFilter
                    teamIds={searchParameters.selectedTeams}
                    onUpdateSelectedTeams={(teamIds: number[]) => {
                        mixpanel.track('Search - content - team', { teamIds })
                        onSearchParameterChange({
                            ...searchParameters,
                            selectedTeams: teamIds,
                            selectedAccountIds: []
                        })
                    }}
                />
            )}
            {showSocialMediaServiceSearch && (
                <SocialMediaServiceFilter
                    onSocialMediaServiceListChange={(socialMediaServiceIds: number[]) => {
                        mixpanel.track('Search - content - social media service', {
                            socialMediaServiceIds
                        })
                        onSearchParameterChange({
                            ...searchParameters,
                            selectedSocialMediaServices: allSocialMediaServices.filter((service) =>
                                socialMediaServiceIds.includes(service.id)
                            )
                        })
                    }}
                    allSocialMediaServices={allSocialMediaServices}
                    selectedSocialMediaServices={searchParameters.selectedSocialMediaServices}
                />
            )}
            {showProfileSearch && (
                <AccountFilter
                    selectedAccountIds={searchParameters.selectedAccountIds}
                    selectedTeamIds={searchParameters.selectedTeams}
                    onUpdateSelectedAccounts={(accounts: AccountRecord[]) => {
                        mixpanel.track('Search - content - profiles', {
                            accountIds: accounts.map((account) => account.id)
                        })
                        onSearchParameterChange({
                            ...searchParameters,
                            selectedAccountIds: accounts.map((account) => account.id)
                        })
                    }}
                />
            )}

            <SummaryStatistics
                onModerationChange={(moderation: string) => {
                    mixpanel.track('Search - content - moderation', {
                        moderation
                    })
                    onSearchParameterChange({ ...searchParameters, moderation })
                }}
                selectedModeration={searchParameters.moderation}
                searchParameters={searchParameters}
            />

            <SearchInput
                keyword={searchParameters.keyword}
                onKeywordChange={(keyword: string) => {
                    mixpanel.track('Search - content - keyword', { keyword })
                    onSearchParameterChange({ ...searchParameters, keyword })
                }}
                onSearch={(keyword: string) => {
                    mixpanel.track('Search - content - keyword', { keyword })
                    onSearchParameterChange({ ...searchParameters, keyword })
                }}
            />

            <DateTimePickerContainer
                startDateTime={searchParameters.startDateTime}
                endDateTime={searchParameters.endDateTime}
                timeDuration={searchParameters.timeDuration}
                onDateChange={(
                    startDateTime?: Dayjs,
                    endDateTime?: Dayjs,
                    timeDuration?: Duration,
                    replace?: boolean
                ) => {
                    if (startDateTime) {
                        mixpanel.track('Search - content - start date time', {
                            startDateTime: startDateTime.toISOString()
                        })
                    }
                    if (endDateTime) {
                        mixpanel.track('Search - content - end date time', {
                            endDateTime: endDateTime.toISOString()
                        })
                    }
                    if (timeDuration) {
                        mixpanel.track('Search - content - duration', {
                            timeDuration: timeDuration.toISOString()
                        })
                    }
                    onSearchParameterChange(
                        {
                            ...searchParameters,
                            startDateTime,
                            endDateTime,
                            timeDuration
                        },
                        replace
                    )
                }}
                useDefault={true}
            />

            {showReadStatusSearch && (
                <ReadReceiptFilterComboBox
                    selection={searchParameters.readReceipt}
                    onChange={(newSelection) => {
                        onSearchParameterChange({
                            ...searchParameters,
                            readReceipt: newSelection
                        })
                    }}
                />
            )}
            <GeneralClassifierFilter
                searchParameters={searchParameters}
                onUpdateSelectedClassifiers={(classifiers: string[]) => {
                    mixpanel.track('Search - content - classifier', {
                        classifiers
                    })
                    onSearchParameterChange({
                        ...searchParameters,
                        selectedGeneralClassifiers: classifiers
                    })
                }}
            />

            <AdOrOrganicFilterComboBox
                selection={searchParameters.adOrOrganic}
                onChange={(newSelection) => {
                    onSearchParameterChange({
                        ...searchParameters,
                        adOrOrganic: newSelection
                    })
                }}
                socialMediaService={socialMediaService}
            />
            {showDmsSearch && (
                <DmFilterComboBox
                    selection={searchParameters.dmFilter}
                    onChange={(newSelection) => {
                        onSearchParameterChange({
                            ...searchParameters,
                            dmFilter: newSelection
                        })
                    }}
                />
            )}

            <div className="flex flex-row items-center">
                <HasMediaToggle
                    hasMedia={searchParameters.hasMedia ?? false}
                    onToggle={(isEnabled: boolean) => {
                        return Promise.resolve(
                            onSearchParameterChange({
                                ...searchParameters,
                                hasMedia: isEnabled || undefined
                            })
                        )
                    }}
                />
            </div>

            <div className="flex flex-row items-center">
                {/** DC: During usability review we switched from "Hide own replies" to "Show own replies" and defaulted to false.
                     Instead of changing the parameter name for query param, graphql query, local storage I just changed the Toggle component itself.
                     Hence the NOTing of the logic in the attributes below.*/}
                <OwnRepliesToggle
                    showOwnComments={!searchParameters.excludeOwnComments}
                    onToggle={(isEnabled: boolean) => {
                        return Promise.resolve(
                            onSearchParameterChange({
                                ...searchParameters,
                                excludeOwnComments: !isEnabled
                            })
                        )
                    }}
                />
            </div>

            <TranslationSettings />

            {user.feedsEnabled && (
                <ContentFeedEditDialog
                    contentFeed={contentFeed}
                    searchParameters={searchParameters}
                />
            )}

            <div className="flex flex-row items-center">
                {/* This component will be used in the second step of this task */}
                {/* <SuggestedRepliesToggle
                    searchParameters={searchParameters}
                    onSearchParameterChange={onSearchParameterChange}
                /> */}
            </div>
            {/* This component will be used in the second step of this task */}
            {/* <RepliesSelectDropDown
                searchParameters={searchParameters}
                onSearchParameterChange={onSearchParameterChange}
            /> */}
        </div>
    )
}
